import React, { useState } from 'react'
import { Modal } from '../ui/Modal/Modal.jsx'
import { Button } from '../ui/Button/Button.jsx'
import { MiniPill } from '../ui/MiniPill/MiniPill.jsx'

/**
 * SubmitPrepModal — S08 submit gate.
 *
 * Calls `submitPrep` (flips Prep.isVisibleToManager = true).
 * Shows per-card visibility chips so the user knows what their manager will see.
 *
 * Phase E. Visibility is currently all-or-nothing (the whole prep becomes visible).
 * Per-card visibility toggles are a Phase J enhancement; this modal shows the
 * cards that will be shared as informational chips, not controls.
 */
export function SubmitPrepModal({ open, onClose, onSubmit, cards = [], loading = false }) {
  const [submitted, setSubmitted] = useState(false)

  async function handleSubmit() {
    await onSubmit()
    setSubmitted(true)
  }

  const coreCards = cards.filter((c) => c.isCore)
  const optionalCards = cards.filter((c) => !c.isCore)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Share prep with your manager"
      width="sm"
    >
      {submitted ? (
        <div style={styles.success}>
          <span style={styles.successIcon}>✓</span>
          <p style={styles.successMsg}>Prep shared. Your manager can now see it.</p>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      ) : (
        <>
          <p style={styles.intro}>
            Your manager will see the cards you&apos;ve filled in. Core cards are always shared.
          </p>

          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>Core cards</h3>
            <div style={styles.chipRow} role="list">
              {coreCards.map((c) => (
                <MiniPill key={c.prepCardId} kind="on-track" role="listitem">{c.kind.replace('prep-', '')}</MiniPill>
              ))}
              {coreCards.length === 0 && <span style={styles.none}>None</span>}
            </div>
          </section>

          {optionalCards.length > 0 && (
            <section style={styles.section}>
              <h3 style={styles.sectionTitle}>Optional cards (filled in)</h3>
              <div style={styles.chipRow} role="list">
                {optionalCards.filter((c) => c.state !== 'empty').map((c) => (
                  <MiniPill key={c.prepCardId} kind="discussed" role="listitem">{c.kind.replace('prep-', '')}</MiniPill>
                ))}
              </div>
            </section>
          )}

          <div style={styles.actions}>
            <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Sharing…' : 'Share with manager'}
            </Button>
          </div>
        </>
      )}
    </Modal>
  )
}

const styles = {
  intro: { fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5, padding: '0 0 16px' },
  section: { marginBottom: 16 },
  sectionTitle: { fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 8 },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  none: { fontSize: 12, color: 'var(--color-text-faint)', fontStyle: 'italic' },
  actions: { display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid var(--color-line)' },
  success: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '16px 0' },
  successIcon: { fontSize: 32, color: 'var(--color-success)' },
  successMsg: { fontSize: 14, color: 'var(--color-text-primary)', textAlign: 'center' },
}
